<template>
  <div v-if="isActive">
    <deal-status-dropdown
      :name="`${type}_dropdown`"
      :options="statusOptions"
      :value="value || null"
      :color="color"
      :is-loading="isDealStatusLoading"
      @update:status="updateDealStatus"
      @click="statusLoading"
    />
  </div>
  <div v-else>-</div>
</template>
<script setup lang="ts">
import { computed } from "vue";
import { useStore } from "vuex";

import isEmpty from "lodash/isEmpty";

import { DEFAULT_WORKFLOW_STATUS_COLOR } from "@/helpers/constants";
import { hexToRgb } from "@/helpers/formatting";
import { usePromiseWrapper } from "@/hooks/common";

import DealStatusDropdown from "@/components/deals/DealStatusDropdown.vue";

import type { IApplication } from "@/models/applications";
import type { ApplicationStatusPayload } from "@/models/applications";
import type { IWorkflowStatus } from "@/models/workflows";

const props = withDefaults(
  defineProps<{
    value: string | null;
    model: IApplication;
    type?: "status" | "sub_status";
  }>(),
  { type: "status" }
);

const { dispatch, getters } = useStore();

const isActive = computed(
  () =>
    props.type !== "sub_status" || !isEmpty(props.model.workflow_status?.status)
);

const isLiveStatus = computed(() =>
  Boolean(props.model.workflow_status?.is_live_status ?? true)
);

const statuses = computed<IWorkflowStatus[]>(() =>
  getters["applications/workflowStatusesByDealId"](props.model.id)
);

const statusOptions = computed(() => {
  const statusesByType = statuses.value?.filter(
    (status) => status.live === isLiveStatus.value
  );

  if (props.type === "status") {
    return (
      statusesByType?.reduce(
        (acc: Record<string, string>, status: IWorkflowStatus) => {
          acc[status.name] = status.name;
          return acc;
        },
        {}
      ) || {}
    );
  }

  return (
    statusesByType
      ?.find((status) => status.name === props.model.workflow_status?.status)
      ?.substatuses?.reduce((acc: Record<string, string>, status: string) => {
        acc[status] = status;
        return acc;
      }, {}) || {}
  );
});

const color = computed(() => {
  const statusColor = String(
    props.model.workflow_status?.color
  ).toLocaleLowerCase();

  return hexToRgb(statusColor) ? statusColor : DEFAULT_WORKFLOW_STATUS_COLOR;
});

const colorByStatusName = (name: ApplicationStatusPayload["status"]) => {
  return (
    statuses.value.find((status: IWorkflowStatus) => status.name === name)
      ?.color ?? DEFAULT_WORKFLOW_STATUS_COLOR
  );
};

const { fetchWrapper: updateDealStatus } = usePromiseWrapper(
  async (status: string | number) => {
    const payload = (
      props.type === "status"
        ? {
            status: String(status),
            sub_status: null,
            color: colorByStatusName(String(status)),
          }
        : {
            status: props.model.workflow_status?.status,
            sub_status: String(status),
            color: props.model.workflow_status?.color,
          }
    ) as ApplicationStatusPayload;

    await dispatch("applications/updateWorkflowStatus", {
      id: props.model.id,
      payload,
      isList: true,
    });
  }
);

const { fetchWrapper: statusLoading, loading: isDealStatusLoading } =
  usePromiseWrapper(async () => {
    if (isEmpty(statuses.value)) {
      await dispatch("applications/getWorkflowStatuses", props.model.id);
    }
  });
</script>
